import { createApp, h } from 'vue';
import axios from 'axios';
import { createInertiaApp, router } from '@inertiajs/vue3';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import altiumPreset from '@/Theme/altiumPreset.js';
import { BRAND_FALLBACK } from '@/Composables/useBranding';
import AppWrapper from '@/Components/AppWrapper.vue';
import AppDate from '@/Components/UI/AppDate.vue';
import AppLocalDate from '@/Components/UI/AppLocalDate.vue';
import {
    CSRF_MISMATCH_STATUS,
    createCsrfFetch,
    createCsrfRecovery,
    installAxiosCsrfRecovery,
    readXsrfCookie,
    requestCsrfToken,
} from '@/Utils/csrfRecovery';
import '../css/app.css';

// CSRF recovery (REM-1710): a 419 renews the token and replays the request once
// instead of showing Laravel's "Page Expired" page.
let currentPage = null;

function safeSessionStorage() {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

const nativeFetch = window.fetch.bind(window);
const csrfRecovery = createCsrfRecovery({
    fetchToken: () => requestCsrfToken(nativeFetch),
    isAuthenticatedPage: () => Boolean(currentPage?.props?.auth?.user),
    navigate: (url) => window.location.assign(url),
    reload: () => window.location.reload(),
    setMetaToken: (token) => document.querySelector('meta[name="csrf-token"]')?.setAttribute('content', token),
    storage: safeSessionStorage(),
});

installAxiosCsrfRecovery(axios, csrfRecovery);
window.fetch = createCsrfFetch(nativeFetch, csrfRecovery, {
    origin: window.location.origin,
    readCookie: () => readXsrfCookie(document.cookie),
});

router.on('navigate', (event) => {
    currentPage = event.detail.page;
});
// Last guard: never show a 419 in Inertia's HTML error modal.
router.on('invalid', (event) => {
    if (event.detail.response?.status === CSRF_MISMATCH_STATUS) {
        event.preventDefault();
    }
});

createInertiaApp({
    title: (title) => title ? `${title} | ${BRAND_FALLBACK}` : BRAND_FALLBACK,
    resolve: (name) => {
        const pages = import.meta.glob('./Pages/**/*.vue');
        return pages[`./Pages/${name}.vue`]();
    },
    setup({ el, App, props, plugin }) {
        currentPage = props.initialPage;

        const app = createApp({
            render: () => h(AppWrapper, null, {
                default: () => h(App, props)
            })
        });

        app.use(plugin);
        app.use(PrimeVue, {
            theme: {
                preset: altiumPreset,
                options: {
                    darkModeSelector: '.dark-mode',
                }
            },
            locale: {
                // General
                accept: 'Aceptar',
                reject: 'Rechazar',
                choose: 'Elegir',
                upload: 'Subir',
                cancel: 'Cancelar',
                clear: 'Limpiar',
                apply: 'Aplicar',
                close: 'Cerrar',
                completed: 'Completado',
                pending: 'Pendiente',

                // Date
                dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
                dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
                dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
                monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
                monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
                today: 'Hoy',
                weekHeader: 'Sem',
                firstDayOfWeek: 1,
                dateFormat: 'dd/mm/yy',
                am: 'a.m.',
                pm: 'p.m.',

                // Empty states
                emptyMessage: 'Sin resultados',
                emptyFilterMessage: 'Sin resultados',
                emptySearchMessage: 'Sin resultados',
                searchMessage: '{0} resultados disponibles',
                selectionMessage: '{0} elementos seleccionados',
                emptySelectionMessage: 'Ningún elemento seleccionado',

                // DataTable filters
                startsWith: 'Comienza con',
                contains: 'Contiene',
                notContains: 'No contiene',
                endsWith: 'Termina con',
                equals: 'Igual a',
                notEquals: 'Diferente de',
                noFilter: 'Sin filtro',
                lt: 'Menor que',
                lte: 'Menor o igual a',
                gt: 'Mayor que',
                gte: 'Mayor o igual a',
                dateIs: 'La fecha es',
                dateIsNot: 'La fecha no es',
                dateBefore: 'Antes de',
                dateAfter: 'Después de',
                matchAll: 'Coincidir todos',
                matchAny: 'Coincidir cualquiera',
                addRule: 'Agregar regla',
                removeRule: 'Eliminar regla',

                // Password
                weak: 'Débil',
                medium: 'Medio',
                strong: 'Fuerte',
                passwordPrompt: 'Ingrese una contraseña',

                // File
                fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],

                // ARIA
                aria: {
                    close: 'Cerrar',
                    previous: 'Anterior',
                    next: 'Siguiente',
                    selectAll: 'Seleccionar todo',
                    unselectAll: 'Deseleccionar todo',
                    pageLabel: 'Página {page}',
                    firstPageLabel: 'Primera página',
                    lastPageLabel: 'Última página',
                    nextPageLabel: 'Siguiente página',
                    prevPageLabel: 'Página anterior',
                },
            },
        });
        app.use(ToastService);
        app.use(ConfirmationService);

        // Global directives
        app.directive('tooltip', Tooltip);

        // Global components
        app.component('AppDate', AppDate);
        app.component('AppLocalDate', AppLocalDate);

        app.mount(el);
    },
});
